import { Injectable } from '@angular/core';

interface AppVersionInfo {
  version: string;
  builtAt: string;
}

@Injectable({ providedIn: 'root' })
export class AppUpdateService {
  private readonly storageKey = 'fusion-backoffice-app-version';
  private readonly checkIntervalMs = 5 * 60 * 1000;
  private started = false;

  init(): void {
    if (this.started || typeof window === 'undefined') {
      return;
    }

    this.started = true;
    this.checkForUpdate(false);
    window.setInterval(() => this.checkForUpdate(true), this.checkIntervalMs);
    window.addEventListener('focus', () => this.checkForUpdate(true));
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        this.checkForUpdate(true);
      }
    });
  }

  private async checkForUpdate(reloadOnChange: boolean): Promise<void> {
    try {
      const version = await this.fetchCurrentVersion();
      if (!version?.version) {
        return;
      }

      const previousVersion = window.localStorage.getItem(this.storageKey);
      window.localStorage.setItem(this.storageKey, version.version);

      if (reloadOnChange && previousVersion && previousVersion !== version.version) {
        await this.refreshServiceWorker();
        window.location.reload();
      }
    } catch (err) {
      console.warn('[AppUpdate] version check failed', err);
    }
  }

  private async fetchCurrentVersion(): Promise<AppVersionInfo | null> {
    const res = await fetch(`/assets/app-version.json?ts=${Date.now()}`, {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
      },
    });

    if (!res.ok) {
      return null;
    }

    return res.json();
  }

  private async refreshServiceWorker(): Promise<void> {
    if (!('serviceWorker' in navigator)) {
      return;
    }

    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((registration) => registration.update()));
  }
}
